import { useEffect } from 'react';
import { Select } from '@/components/ui/Select';
import { useActiveStores } from '@/hooks/useActiveStores';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setSelectedStore } from '@/store/appSlice';
import { notify } from '@/core/toast/notify';
import { clearPosSessionStorage } from '@/utils/posSessionStorage';

interface PosStoreSelectorProps {
  /** Hide the field label — for compact headers */
  compact?: boolean;
  className?: string;
}

export function PosStoreSelector({ compact = true, className }: PosStoreSelectorProps) {
  const dispatch = useAppDispatch();
  const selected = useAppSelector((s) => s.app.selectedStore);
  const { stores, loading } = useActiveStores();

  useEffect(() => {
    if (selected || loading || stores.length !== 1) return;
    dispatch(setSelectedStore(stores[0]));
  }, [dispatch, loading, selected, stores]);

  useEffect(() => {
    if (!selected || loading || stores.length === 0) return;
    if (!stores.some((s) => s.id === selected.id)) {
      clearPosSessionStorage();
      dispatch(setSelectedStore(null));
    }
  }, [dispatch, loading, selected, stores]);

  const onChange = (value: string) => {
    const store = stores.find((s) => String(s.id) === value);
    if (!store || store.id === selected?.id) return;
    clearPosSessionStorage();
    dispatch(setSelectedStore(store));
    notify.success(`Working in ${store.name}`);
  };

  return (
    <Select
      label={compact ? undefined : 'Store'}
      className={className ?? 'min-w-[9rem] max-w-[15rem]'}
      placeholder={loading ? 'Loading stores…' : 'Select store'}
      disabled={loading || stores.length === 0}
      value={selected ? String(selected.id) : ''}
      options={stores.map((s) => ({
        value: String(s.id),
        label: `${s.name} (${s.code})`,
      }))}
      onChange={onChange}
    />
  );
}
